import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Key, Webhook, ShieldOff } from "lucide-react";
import { toast } from "sonner";

export default function AdminApiPortal() {
  const [keys, setKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const load = async () => {
    try {
      setKeys((await api.get("/admin/api-keys")).data.rows);
      setWebhooks((await api.get("/admin/webhooks")).data.rows);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    if (!confirm("Revoke this key permanently?")) return;
    try { await api.post(`/admin/api-keys/${id}/revoke`); toast.success("Key revoked"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AdminLayout subtitle="Module 12 · Commerce" title="API & Webhooks Overview">
      <Card className="rounded-sm border-white/10 bg-white/[0.02] mb-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <Key className="w-5 h-5 text-ec-emerald" />
            <h3 className="font-display text-xl font-bold text-white">All API keys</h3>
            <Badge className="bg-white/10 border-white/10 rounded-sm">{keys.length}</Badge>
          </div>
          {keys.length === 0 ? (
            <div className="py-8 text-center text-zinc-500" data-testid="apikeys-empty">No API keys issued yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="apikeys-table">
              <thead className="bg-white/5">
                <tr className="text-left">
                  <th className="px-4 py-3 eyebrow text-zinc-500">Name</th>
                  <th className="px-4 py-3 eyebrow text-zinc-500">Owner</th>
                  <th className="px-4 py-3 eyebrow text-zinc-500">Prefix</th>
                  <th className="px-4 py-3 eyebrow text-zinc-500">Limits</th>
                  <th className="px-4 py-3 eyebrow text-zinc-500">Status</th>
                  <th className="px-4 py-3 eyebrow text-zinc-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className="border-t border-white/5" data-testid={`apikey-row-${k.id}`}>
                    <td className="px-4 py-3 text-white">{k.name}</td>
                    <td className="px-4 py-3 text-xs">{k.owner_email}</td>
                    <td className="px-4 py-3 font-mono-plex text-xs text-zinc-400">{k.prefix}…</td>
                    <td className="px-4 py-3 text-xs font-mono-plex">{k.rate_limit_per_minute}/min · {k.monthly_used}/{k.monthly_limit} mo</td>
                    <td className="px-4 py-3">{k.revoked ? <Badge className="bg-ec-error/20 text-ec-error border-ec-error/40 rounded-sm">Revoked</Badge> : <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/40 rounded-sm">Active</Badge>}</td>
                    <td className="px-4 py-3 text-right">
                      {!k.revoked && <Button size="sm" variant="outline" className="rounded-sm h-7 border-ec-error/40 text-ec-error hover:bg-ec-error/10 gap-1" onClick={() => revoke(k.id)} data-testid={`apikey-revoke-${k.id}`}><ShieldOff className="w-3 h-3" />Revoke</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <Webhook className="w-5 h-5 text-ec-emerald" />
            <h3 className="font-display text-xl font-bold text-white">All webhooks</h3>
            <Badge className="bg-white/10 border-white/10 rounded-sm">{webhooks.length}</Badge>
          </div>
          {webhooks.length === 0 ? (
            <div className="py-8 text-center text-zinc-500" data-testid="webhooks-empty">No webhooks configured.</div>
          ) : (
            <ul className="divide-y divide-white/5 text-sm" data-testid="webhooks-list">
              {webhooks.map((w) => (
                <li key={w.id} className="py-3" data-testid={`webhook-row-${w.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono-plex text-zinc-300 flex-1 truncate">{w.url}</span>
                    <Badge className="bg-white/10 border-white/10 rounded-sm text-[10px]">{w.events.length} events</Badge>
                    {w.active ? <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/40 rounded-sm">Active</Badge> : <Badge className="bg-white/5 text-zinc-500 border-white/10 rounded-sm">Inactive</Badge>}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1 font-mono-plex">owner · {w.owner_email}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
